package com.neurobs.ccabserver.translationadmin.components.translationprogress.components

import com.neurobs.ccab.translation.{CCABTestFile, CCABTestPhrase, OrderedTranslationPhraseData, TranslatorDataView, TranslatorProgressData}
import com.neurobs.ccabserver.translationadmin.states.{TestUploadStateGenerator, TranslationProgressStateGenerator}
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{ReactElementFactory, ServerDataAttribute}
import com.neurobs.react.semanticui.{UiButtonFactory, UiDividerFactory, UiInputFactory, UiLoaderFactory, UiMessageFactory}
import org.scalajs.dom
import org.scalajs.dom.ProgressEvent
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.{React, ReactElement}
import slinky.web.html._
import io.circe.generic.auto._
import com.neurobs.util.JsonPrinter.jsonPrint

import scala.scalajs.js

@react
object TranslatorProgressCards {
  case class Props(enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>

    val translationProgressState = TranslationProgressStateGenerator.accessState

    props.enclosingElement(
      Seq(
        ReactElementFactory.Div(
          translationProgressState.selectedTranslatorsState.value.map(selectedTranslator => {
            TranslatorProgressCard(selectedTranslator)
          })
        )("ui cards")
      )
    )

  }

}

@react
object TranslatorProgressCard {

  case class Props(translatorDataView: TranslatorDataView)
  val component = FunctionalComponent[Props] { props =>

    val testUploadState = TestUploadStateGenerator.accessState
    val translationProgressState = TranslationProgressStateGenerator.accessState
    val serverProgressDataState = ServerDataAttribute(useState[Either[Option[String], TranslatorProgressData]](Left(None)))

    def clear = () => {
      translationProgressState.clearTranslator(props.translatorDataView)
    }

    useEffect(() => {
      loadProgressData()
    }, Vector())

    def loadProgressData() = {
      testUploadState.productVersionState.value.map(_.map(productVersionData => {
        TranslationServer.getTranslatorProgress(props.translatorDataView.id, productVersionData.id)(handleProgressData)
      }))
    }

    def handleProgressData = ServerResponseUtil.handleObjectResultResponse[TranslatorProgressData](
      handleObject = serverProgressDataState.setData(_),
      onAnyError = serverProgressDataState.setError(_)
    )



    div(className := "card")(
      div(className := "content")(
        UiButtonFactory.IconButton(
          handleClick = clear,
          iconName = "close",
        )("red mini right floated"),
        div(className := "header")(props.translatorDataView.username),
        div(className := "meta")(props.translatorDataView.languageDisplayName),

        div(className := "description")(
          serverProgressDataState.value match {
            case Left(errorOpt) => errorOpt match {
              case None => UiLoaderFactory.ActiveTextLoader("Loading translator progress...")("mini")
              case Some(error) => UiMessageFactory.BasicErrorMessage(error)
            }
            case Right(data) =>
              s"${data.translatedPhrases} of ${data.totalPhrases} phrases translated"
          }
        ),
      ),
      CCABTranslationUpload(
        props.translatorDataView,
        div(className := "extra content")(_)
      )
    )

  }
}

@react
object CCABTranslationUpload {
  case class Props(translatorDataView: TranslatorDataView, enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>

    val testUploadState = TestUploadStateGenerator.accessState
    val fileInputRef = React.createRef[dom.html.Input]
    val (jsonFileString, setJsonFileString) = useState[Either[Option[String], String]](Left(None))
    val (loadingTranslation, setLoadingTranlation) = useState(false)

    def uploadedFile = {
      if (fileInputRef.current == null || fileInputRef.current.files.length == 0) None
      else Some(fileInputRef.current.files.item(0))
    }

    def readUploadedFile: () => Unit = () => {
      uploadedFile.map(file => {
        val fileReader = new dom.FileReader()
        fileReader.readAsText(file)
        fileReader.onload = (e: ProgressEvent) => {
          val fileString = fileReader.result.asInstanceOf[String]
          val parsed = io.circe.parser.decode[List[CCABTestPhrase]](fileString)
          parsed match {
            case Right(data) =>
              translateTest(data)
            case Left(error) =>
              setJsonFileString(Left(Some("The selected file is improperly formatted")))
          }
        }
      })
    }

    def translateTest(testPhrases: List[CCABTestPhrase]) = {
      testUploadState.productVersionState.value.map(_.map(productVersionData => {
        setLoadingTranlation(true)
        TranslationServer.getProductVersionTranslations(productVersionData.id, props.translatorDataView.id)(handleServerTranslations(testPhrases))
      }))
    }

    def handleServerTranslations(testPhrases: List[CCABTestPhrase]) = ServerResponseUtil.handleObjectResultResponse[Seq[OrderedTranslationPhraseData]](
      handleObject = translatedPhrases => processTranslations(testPhrases, translatedPhrases.toList),
      onAnyError = error => setJsonFileString(Left(Some(error))),
      onAnyResponse = () => setLoadingTranlation(false)
    )

    def processTranslations(testPhrases: List[CCABTestPhrase], translatedPhrases: List[OrderedTranslationPhraseData]) = {
      val processedPhrases = CCABTestFile.processTranslations(testPhrases, translatedPhrases)
      setJsonFileString(Right(jsonPrint(processedPhrases)))
    }

    def inputStyle = Seq[(String, js.Any)](("width", "100%"))
    def aStyle = Seq[(String, js.Any)](("marginLeft", "30px"))

    props.enclosingElement(
      Seq[ReactElement](
        UiInputFactory.FileInput("Upload test file", fileInputRef, ".json")(inputStyle),
        UiDividerFactory.Horizontal,
        UiButtonFactory.Button("Translate test", handleClick = readUploadedFile)("mini"),
        if (loadingTranslation) ReactElementFactory.P("Translating test...")(aStyle)
        else {
          jsonFileString match {
            case Left(errorOpt) => errorOpt match {
              case None => null
              case Some(error) => UiMessageFactory.BasicErrorMessage(error)
            }
            case Right(fileString) =>
              ReactElementFactory.A(
                text = "Download",
                href = "data:text/json;charset=utf-8," + scalajs.js.URIUtils.encodeURIComponent(fileString),
                target = "_blank",
                download = true
              )(aStyle)
          }
        }
      )
    )

  }
}
